import { Box, styled, Typography } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import {
  SAIComparisonTable,
  SAIDIalogContainer
} from '@setvi/shared/components';
import { AIContentBox } from '@setvi/shared/components/ai/dialog-container/styled';
import { Product } from '@setvi/shared/interfaces';
import { useQuery } from '@tanstack/react-query';
import { recommendProductsMutation } from 'Services';

import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useProductMetadata } from 'pages/products/hooks/use-product-metadata';
import { aiRecommendMessages } from 'pages/products/components/ai/loader/messages';

import { DialogLoader, Error } from '../../../components';

const Title = styled(Typography)(({ theme: { spacing } }) => ({
  fontSize: 20,
  fontWeight: 'bold',
  textDecoration: 'underline',
  marginBottom: spacing(1)
}));

interface RecommendationAnalysisProps {
  products: Product[];
  purpose: string;
}

const textStyle = {
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.5
};

export const RecommendationAnalysis = ({
  products,
  purpose
}: RecommendationAnalysisProps) => {
  const { user } = useAppContext();
  const { isLoading: isMetadataLoading, handleExternalMetadata } =
    useProductMetadata({});

  const [meta, setMeta] = useState([]);
  const [loading, setLoading] = useState(false);

  const isInvalid = useMemo(
    () =>
      products.some(
        p =>
          !p?.vendor?.name ||
          (user?.IsManufacturer ? !p?.sku : !p?.manufacturerSku)
      ),
    [products, user]
  );

  const { data, isFetching: isRecommendLoading } = useQuery({
    ...recommendProductsMutation({
      purpose,
      products: meta
    }),
    enabled: !!meta?.length
  });

  const handleMetadata = async (prods: Product[]) => {
    setLoading(true);

    const results = await Promise.all(
      prods.map(async p => {
        const metadata = await handleExternalMetadata(p);
        if (!metadata) return null;
        return {
          _id: p?._id,
          metadata
        };
      })
    );

    setMeta(results.filter(Boolean)); // Remove nulls
    setLoading(false);
  };

  useEffect(() => {
    if (products?.length && !isInvalid) {
      handleMetadata(products);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const isLoading = useMemo(
    () => isMetadataLoading || isRecommendLoading || loading,
    [isMetadataLoading, isRecommendLoading, loading]
  );

  return (
    <SAIDIalogContainer
      headerDivider={false}
      title="AI Recommendation Analysis">
      <AIContentBox display="flex" flexDirection="column" gridGap="16px">
        {isLoading ? (
          <DialogLoader messages={aiRecommendMessages} />
        ) : (
          <>
            {data?.error || !data || isInvalid ? (
              <Error
                value={
                  data?.error ||
                  'Not Enough Information to Recommendation Analysis'
                }
              />
            ) : (
              <>
                {data.briefDescription && (
                  <Box>
                    <Title>Recommendation Analysis</Title>
                    <Box
                      style={textStyle}
                      dangerouslySetInnerHTML={{
                        __html: data.briefDescription
                      }}
                    />
                  </Box>
                )}
                <SAIComparisonTable
                  features={data.table.map((i: any) => {
                    const { feature, ...rest } = i;
                    return {
                      name: feature,
                      values: Object.keys(rest).map(j => i[j as keyof typeof i])
                    };
                  })}
                  products={products.map(i => i?.name)}
                />
                {data.finalRecommendation && (
                  <Box>
                    <Title>Final Recommendation</Title>
                    <Box
                      style={textStyle}
                      dangerouslySetInnerHTML={{
                        __html: data.finalRecommendation
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </AIContentBox>
    </SAIDIalogContainer>
  );
};
